import React, { useState } from "react";
import { Plus, X, Eye, EyeOff, Wifi, WifiOff, Settings } from "lucide-react";

const dummyCameras = [
  {
    id: 1,
    name: "Pond A - Feeding Zone",
    ip: "192.168.1.101",
    username: "admin",
    password: "password123",
    channel: "1",
    image: "/pondA.jpg",
    status: "connected",
    resolution: "1080p",
    fps: 30,
    lastFrame: "Just now",
  },
  {
    id: 2,
    name: "Pond B - Aerator Zone",
    ip: "192.168.1.102",
    username: "admin",
    password: "password123",
    channel: "2",
    image: "/pondB.jpg",
    status: "connected",
    resolution: "1080p",
    fps: 30,
    lastFrame: "Just now",
  },
  {
    id: 3,
    name: "Feed Storage Room",
    ip: "192.168.1.103",
    username: "admin",
    password: "password123",
    channel: "3",
    image: "/feed.jpg",
    status: "connected",
    resolution: "720p",
    fps: 25,
    lastFrame: "2s ago",
  },
  {
    id: 4,
    name: "Entry Gate",
    ip: "192.168.1.104",
    username: "admin",
    password: "password123",
    channel: "4",
    image: "/entry.jpg",
    status: "disconnected",
    resolution: "1080p",
    fps: 30,
    lastFrame: "5m ago",
  },
];

const Camera = () => {
  const [cameras, setCameras] = useState(dummyCameras);
  const [selectedCam, setSelectedCam] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedForPassword, setSelectedForPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name: "",
    ip: "",
    username: "",
    password: "",
    channel: "",
  });

  const handleAddCamera = () => {
    if (form.name && form.ip && form.username && form.password && form.channel) {
      const newCamera = {
        id: cameras.length + 1,
        ...form,
        image: "/default-cam.jpg",
        status: "connecting",
        resolution: "1080p",
        fps: 30,
        lastFrame: "Initializing...",
      };
      setCameras([...cameras, newCamera]);
      setForm({ name: "", ip: "", username: "", password: "", channel: "" });
      setShowAddModal(false);
    }
  };

  const handleViewPassword = (cam) => {
    setSelectedForPassword(cam);
    setShowPasswordModal(true);
  };

  const connectedCount = cameras.filter((c) => c.status === "connected").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Camera Management</h1>
          <p className="text-gray-500 mt-1">
            {connectedCount} of {cameras.length} cameras connected
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition"
        >
          <Plus size={18} />
          Add Camera
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        {cameras.map((cam) => (
          <div
            key={cam.id}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
          >
            <div className="relative">
              <img
                src={cam.image}
                alt={cam.name}
                className="w-full h-48 object-cover"
              />

              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                <button
                  onClick={() => setSelectedCam(cam)}
                  className="bg-white rounded-lg p-3 hover:bg-gray-100 transition"
                >
                  <Eye size={20} className="text-gray-900" />
                </button>
              </div>

              <div
                className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                  cam.status === "connected"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {cam.status === "connected" ? (
                  <Wifi size={14} />
                ) : (
                  <WifiOff size={14} />
                )}
                {cam.status === "connected" ? "Connected" : "Offline"}
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 text-base">
                  {cam.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  IP: {cam.ip} | CH: {cam.channel}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-500">Resolution</span>
                  <p className="font-semibold text-gray-900">{cam.resolution}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-500">FPS</span>
                  <p className="font-semibold text-gray-900">{cam.fps}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-500">Last Frame</span>
                  <p className="font-semibold text-gray-900">{cam.lastFrame}</p>
                </div>
              </div>

              <button
                onClick={() => handleViewPassword(cam)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition"
              >
                <Settings size={16} />
                View Credentials
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedCam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCam.name}
              </h2>
              <button
                onClick={() => setSelectedCam(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <img
              src={selectedCam.image}
              alt="preview"
              className="w-full h-64 object-cover rounded-lg"
            />

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-semibold text-gray-900">
                    {selectedCam.status === "connected"
                      ? "Connected"
                      : "Disconnected"}
                  </p>
                </div>
                <div
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                    selectedCam.status === "connected"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {selectedCam.status === "connected" ? (
                    <Wifi size={14} />
                  ) : (
                    <WifiOff size={14} />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">IP Address</p>
                  <p className="font-mono font-semibold text-gray-900 text-sm mt-1">
                    {selectedCam.ip}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Channel</p>
                  <p className="font-semibold text-gray-900 text-sm mt-1">
                    {selectedCam.channel}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Resolution</p>
                  <p className="font-semibold text-gray-900 text-sm mt-1">
                    {selectedCam.resolution}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Frame Rate</p>
                  <p className="font-semibold text-gray-900 text-sm mt-1">
                    {selectedCam.fps} FPS
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedCam(null)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2.5 rounded-lg font-medium transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showPasswordModal && selectedForPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Camera Credentials
              </h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              {selectedForPassword.name}
            </p>

            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">IP Address</p>
                <p className="font-mono text-sm font-semibold text-gray-900">
                  {selectedForPassword.ip}
                </p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Username</p>
                <p className="font-mono text-sm font-semibold text-gray-900">
                  {selectedForPassword.username}
                </p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Password</p>
                <div className="flex items-center justify-between">
                  <p className="font-mono text-sm font-semibold text-gray-900">
                    {showPassword
                      ? selectedForPassword.password
                      : "••••••••"}
                  </p>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-500 hover:text-gray-700 transition"
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowPasswordModal(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Add Camera</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Camera Name
                </label>
                <input
                  placeholder="e.g., Pond A - Feeding Zone"
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  IP Address
                </label>
                <input
                  placeholder="192.168.1.100"
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.ip}
                  onChange={(e) => setForm({ ...form, ip: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Username
                </label>
                <input
                  placeholder="admin"
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.username}
                  onChange={(e) =>
                    setForm({ ...form, username: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Channel
                </label>
                <input
                  placeholder="1"
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.channel}
                  onChange={(e) =>
                    setForm({ ...form, channel: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 py-2.5 rounded-lg font-medium transition"
              >
                Cancel
              </button>

              <button
                onClick={handleAddCamera}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition"
              >
                Add Camera
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Camera;